import { FC } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CustomButton from '../CustomButton';
import styles from './ConfirmDeleteModal.module.scss';

export type ConfirmDeleteModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  isConfirming?: boolean;
  zIndex?: number;
};

const modalPaperSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 'fit-content',
  minWidth: 355,
  width: '100%',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  p: '32px 32px',
  overflowY: 'auto',
  boxShadow: 'none',
  maxHeight: '90vh',
  '@media (max-width: 900px)': {
    p: '32px 15px',
  },
};

const ConfirmDeleteModal: FC<ConfirmDeleteModalProps> = ({
  open,
  onClose,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  onConfirm,
  isConfirming = false,
  zIndex,
}) => {
  const handleConfirm = () => {
    void Promise.resolve(onConfirm());
  };

  return (
    <Modal
      open={open}
      onClose={() => !isConfirming && onClose()}
      aria-labelledby="confirm-delete-modal-title"
      aria-describedby="confirm-delete-modal-description"
      sx={{
        ...(zIndex != null ? { zIndex } : {}),
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box sx={modalPaperSx}>
        <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
          <CloseIcon
            sx={{ width: '30px', height: '30px' }}
            onClick={() => !isConfirming && onClose()}
          />
        </Box>
        <Box className={styles.modalContentWrapper}>
          <Typography
            id="confirm-delete-modal-title"
            variant="h5"
            sx={{ fontWeight: 'bold', textAlign: 'center', color: '#222B3A' }}
          >
            {title}
          </Typography>
          <Typography
            id="confirm-delete-modal-description"
            variant="body1"
            sx={{ color: '#222B3A', textAlign: 'center' }}
          >
            {message}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              gap: '16px',
              '& button': {
                height: '44px',
              },
            }}
          >
            <CustomButton
              variant="outlined"
              onClick={onClose}
              disabled={isConfirming}
              customClass={styles.cancelBtn}
            >
              {cancelLabel}
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={handleConfirm}
              disabled={isConfirming}
              customClass={styles.confirmDanger}
            >
              {isConfirming ? 'Deleting…' : confirmLabel}
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmDeleteModal;
